import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { rbacApi, AuthMeResponse } from '../services/api/rbac';
import { useWebSocket } from './WebSocketContext';

export type UserRole = 'viewer' | 'operator' | 'admin' | 'system_admin';

// Stable empty reference: `?? []` would put a new array into the context value every render,
// re-rendering every consumer of useRole().
const NO_GROUPS: string[] = [];

export const ROLE_LABELS: Record<UserRole, string> = {
  viewer: 'Viewer',
  operator: 'Operator',
  admin: 'Admin',
  system_admin: 'System Admin',
};

const ROLE_HIERARCHY: Record<UserRole, number> = {
  viewer: 0,
  operator: 1,
  admin: 2,
  system_admin: 3,
};

export function hasMinimumRole(userRole: UserRole | null, requiredRole: UserRole): boolean {
  if (!userRole) return false;
  return (ROLE_HIERARCHY[userRole] ?? -1) >= (ROLE_HIERARCHY[requiredRole] ?? Infinity);
}

export interface RoleContextValue {
  role: UserRole | null;
  email: string | null;
  displayName: string | null;
  groups: string[];
  loading: boolean;
  error: string | null;
  hasMinimumRole: (required: UserRole) => boolean;
  refresh: () => Promise<void>;
}

const RoleContext = createContext<RoleContextValue>({
  role: null,
  email: null,
  displayName: null,
  groups: [],
  loading: true,
  error: null,
  hasMinimumRole: () => false,
  refresh: async () => {},
});

export const useRole = () => useContext(RoleContext);

interface RoleProviderProps {
  children: React.ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const { subscribe, connectionStatus } = useWebSocket();

  // THUB-299: was six useState values written by a fetchRole callback plus a mount effect.
  //
  // `retry: false` is deliberate and is the one option that must not be left to the default here.
  // The original made a single attempt and, on failure, dropped the user to viewer. TanStack would
  // otherwise retry three times, which keeps `loading` true for seconds on a real outage -- for
  // the RBAC gate, reaching the read-only fallback promptly is the safer failure mode, and
  // changing the retry policy of an authorization check should be a conscious decision rather
  // than a side effect of a refactor.
  const authQuery = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => rbacApi.getAuthMe(),
    retry: false,
  });

  const data: AuthMeResponse | undefined = authQuery.data;

  // SECURITY: on a failed read, fall back to 'viewer' -- read-only -- exactly as before ("Default
  // to viewer on error so app is still usable (read-only)"). Never fall back to a null role that
  // some caller might treat as permissive, and never leave a previously-elevated role in place.
  const role: UserRole | null = authQuery.error
    ? 'viewer'
    : ((data?.role as UserRole | undefined) ?? null);
  const email = data?.email ?? null;
  const displayName = data?.displayName || null;
  const groups = data?.groups ?? NO_GROUPS;

  // isPending, NOT isFetching: a background re-read (focus, or a role_mapping_changes broadcast)
  // must not flip the app back to a loading state and unmount the UI under the user.
  const loading = authQuery.isPending;
  const error = authQuery.error
    ? ((authQuery.error as { message?: string }).message || 'Failed to fetch role')
    : null;

  const { refetch } = authQuery;
  const fetchRole = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Re-fetch role when role mappings change (admin may have updated permissions).
  //
  // This subscription STAYS. App.tsx deliberately does not subscribe role_mapping_changes -- it
  // maps to none of the entity query keys -- so this is the only thing that carries a revoked
  // permission to an already-open tab.
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    const unsub = subscribe('role_mapping_changes', () => {
      void fetchRole();
    });
    return unsub;
  }, [connectionStatus, subscribe, fetchRole]);

  const checkMinimumRole = useCallback(
    (required: UserRole) => hasMinimumRole(role, required),
    [role]
  );

  return (
    <RoleContext.Provider
      value={{
        role,
        email,
        displayName,
        groups,
        loading,
        error,
        hasMinimumRole: checkMinimumRole,
        refresh: fetchRole,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};

export { RoleContext };
